/** KYC 证件照常见静态存储域名（S3 / CDN），浏览器直连会因 CORS 失败 */
const KYC_STATIC_IMAGE_HOST_PATTERNS = [
  /^spikex[\w-]*\.s3(\.[a-z0-9-]+)?\.amazonaws\.com$/i,
  /^ai-exchange-s3\.s3(\.[a-z0-9-]+)?\.amazonaws\.com$/i,
  /^a-dev\.sigma115\.com$/i,
  /^a\.spikex\.com$/i,
];

export const KYC_IMAGE_PROXY_PATH = "/api/kyc-image-proxy";

export function resolveKycStaticImageHost() {
  const cdn = String(process.env.NEXT_PUBLIC_cdn || "").trim();
  const match = cdn.match(/^\/\/([^/]+)/);
  if (match?.[1]) return match[1];
  return "a.spikex.com";
}

/** 将 uploadPath（如 /1/user/common/xxx.png）还原为静态存储完整 URL */
export function buildKycStaticImageUrl(relativePath) {
  const raw = String(relativePath || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith("//")) return `https:${raw}`;
  const pathname = raw.startsWith("/") ? raw : `/${raw}`;
  return `https://${resolveKycStaticImageHost()}${pathname}`;
}

/** 预览展示用：直接返回可给 <img src> 的地址（不走代理；img 不需要 CORS） */
function normalizeKycPreviewDisplayUrl(url) {
  const raw = String(url || "").trim();
  if (!raw) return "";
  if (raw.startsWith("blob:") || raw.startsWith("data:")) return raw;
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith("//")) return `https:${raw}`;
  return raw;
}

/**
 * 预览用 URL（给 <img>）：
 * 1) blob/data 即时预览（当次上传）
 * 2) KYC 预填完整 URL（直连展示）
 * 3) 用户上传 uploadPath → 当前环境 CDN 直链
 *
 * 注意：提交时若需 fetch 拉图，仍走 resolveKycImageFetchUrl（同源代理）。
 */
export function resolveKycUploadPreviewUrl(item) {
  if (!item) return "";

  const direct = String(item.thumbUrl || item.url || "").trim();
  if (direct.startsWith("blob:") || direct.startsWith("data:")) return direct;

  // KYC 预填：接口完整 URL，直接展示
  if (item.isKycPrefill && direct) {
    return normalizeKycPreviewDisplayUrl(direct);
  }

  const uploadPath = String(item.uploadPath || "").trim();
  if (uploadPath) {
    const staticUrl = buildKycStaticImageUrl(uploadPath);
    if (staticUrl) return staticUrl;
  }

  if (direct) {
    return normalizeKycPreviewDisplayUrl(direct);
  }

  return "";
}

export function hydrateKycUploadFileList(fileList, options) {
  if (!Array.isArray(fileList) || !fileList.length) return fileList;
  const item = fileList[fileList.length - 1];
  if (!item) return fileList;

  const previewUrl = resolveKycUploadPreviewUrl(item, options);
  if (!previewUrl) return fileList;

  const currentUrl = String(item.url || item.thumbUrl || "").trim();
  if (currentUrl === previewUrl) return fileList;

  return [{ ...item, status: item.status || "done", url: previewUrl, thumbUrl: previewUrl }];
}

export function isKycStaticStorageHost(hostname) {
  const host = String(hostname || "").trim().toLowerCase();
  if (!host) return false;
  return KYC_STATIC_IMAGE_HOST_PATTERNS.some((pattern) => pattern.test(host));
}

export function isKycStaticStorageUrl(url) {
  try {
    const parsed = new URL(String(url || "").trim());
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return false;
    return isKycStaticStorageHost(parsed.hostname);
  } catch (_) {
    return false;
  }
}

export function buildKycImageProxyUrl(url, origin) {
  const normalized = String(url || "").trim();
  const base = origin || (typeof window !== "undefined" ? window.location.origin : "");
  return `${base}${KYC_IMAGE_PROXY_PATH}?url=${encodeURIComponent(normalized)}`;
}

/** 将 KYC 静态图 URL 转为可同源 fetch 的地址（仅提交拉图时用；S3 直连 fetch 会 CORS） */
export function resolveKycImageFetchUrl(url, { getOrigin, isLocalDevHost, windowOrigin } = {}) {
  const raw = String(url || "").trim();
  if (!raw) return "";

  let normalized = raw;
  if (/^\/\//.test(raw)) {
    normalized = `https:${raw}`;
  } else if (raw.startsWith("/") && windowOrigin) {
    normalized = `${windowOrigin}${raw}`;
  }

  if (isKycStaticStorageUrl(normalized) && windowOrigin) {
    return buildKycImageProxyUrl(normalized, windowOrigin);
  }

  if (typeof getOrigin === "function" && typeof isLocalDevHost === "function" && windowOrigin && isLocalDevHost()) {
    try {
      const target = new URL(normalized, windowOrigin);
      const wwwOrigin = new URL(getOrigin("www"));
      if (target.host === wwwOrigin.host) {
        return `${windowOrigin}${target.pathname}${target.search}${target.hash}`;
      }
    } catch (_) {
      /* ignore */
    }
  }

  return normalized;
}
